import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Role, roleDisplayMap, roles } from "@/helpers/role-permission";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { Fragment, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useSetAccessCell } from "../hooks/use-access";
import { describeAccessChange } from "./access-change-toast";
import { AccessMatrix, AreaAccess, Capability } from "../services/access";

type MatrixRole = AccessMatrix["roles"][number];

const label = (name: string) => roleDisplayMap[name as Role] ?? name;

/** "Supply Config" -> "supply-config", the token that names an open group in the URL. */
const groupKey = (group: string) => group.toLowerCase().replace(/\s+/g, "-");

const openGroupsFrom = (params: URLSearchParams) =>
  new Set((params.get("open") ?? "").split(",").filter(Boolean));

/** Sidebar-dropdown order, not the alphabetical order the API returns. */
const byRoleOrder = (a: MatrixRole, b: MatrixRole) => {
  const rank = (r: MatrixRole) => {
    const i = roles.indexOf(r.name as Role);
    return i === -1 ? roles.length : i;
  };
  return rank(a) - rank(b) || a.name.localeCompare(b.name);
};

/**
 * Role first, then the grid — one role's View/Edit per area at a time.
 *
 * The selected role lives in the URL (?role=admin) so a refresh, the back
 * button and a shared link all land on the same role. Defaults to the first
 * role that has switches, since a super role has nothing to change.
 */
export function AccessMatrixTable({ matrix }: { matrix: AccessMatrix }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const ordered = [...matrix.roles].sort(byRoleOrder);
  const requested = searchParams.get("role");
  const selected =
    ordered.find((r) => r.name === requested) ??
    ordered.find((r) => !r.is_super) ??
    ordered[0];

  const pick = (name: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("role", name);
        return next;
      },
      { replace: true },
    );

  if (!selected) {
    return <p className="text-sm text-muted-foreground">No roles found.</p>;
  }

  const viewCount = matrix.areas.filter(
    (a) => selected.cells[a.key]?.view,
  ).length;
  const editCount = matrix.areas.filter(
    (a) => selected.cells[a.key]?.edit,
  ).length;

  return (
    <div className="space-y-4">
      {/* Heading for the chosen role on the left, the picker on the right. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-0.5">
          <h2 className="text-base font-medium">{label(selected.name)}</h2>
          <p className="text-xs text-muted-foreground">
            {selected.is_super
              ? `Full access to all ${matrix.areas.length} areas`
              : `Can view ${viewCount} of ${matrix.areas.length} areas · can edit ${editCount}`}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="access-role" className="text-xs text-muted-foreground">
            Role
          </Label>
          <Select value={selected.name} onValueChange={pick}>
            <SelectTrigger id="access-role" className="w-[240px]">
              <SelectValue placeholder="Choose a role" />
            </SelectTrigger>
            <SelectContent>
              {ordered.map((role) => (
                <SelectItem key={role.id} value={role.name}>
                  <span className="flex items-center gap-2">
                    {label(role.name)}
                    {role.is_super && (
                      <span className="text-xs text-muted-foreground">
                        full access
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selected.is_super ? (
        <SuperRoleNotice role={selected} />
      ) : (
        <RoleGrid role={selected} areas={matrix.areas} />
      )}
    </div>
  );
}

/**
 * A super role always has everything, including areas added later, so it is
 * described rather than rendered as switches — switches would imply a control
 * that does not exist.
 */
function SuperRoleNotice({ role }: { role: MatrixRole }) {
  return (
    <div className="flex items-start gap-3 rounded-md border bg-muted/40 px-4 py-3">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="space-y-1 text-sm">
        <p className="font-medium">
          {label(role.name)} always has full access, in every region.
        </p>
        <p className="text-muted-foreground">
          That includes areas added in future, so there is nothing to switch
          here. To change what one person can do, change their role on the
          Internal Users page instead.
        </p>
      </div>
    </div>
  );
}

type AreaRow = AccessMatrix["areas"][number];
type GridEntry =
  | { kind: "single"; area: AreaRow }
  | { kind: "group"; label: string; areas: AreaRow[] };

/**
 * Both columns are drawn, and both switches stay enabled, for every area —
 * including the handful whose pages have no write route today (Decorator Links,
 * Export, Invoices, OTP Logs, Duplicate Accounts). There, View is the
 * authoritative half: every route on those pages asks for `read`, so the guard
 * derives "view" and never consults can_edit. Ticking Edit has exactly one
 * immediate effect, the server's Edit-implies-View rule, which turns View on.
 *
 * Kept that way by decision rather than greyed out: the cell costs nothing, it
 * becomes a live grant the day one of those areas gains a write route — no
 * migration, no change here — and disabling it would need a rule the care team
 * cannot see on this screen. To be revisited with the team.
 */
const CAPABILITIES: Capability[] = ["view", "edit"];

/** Consecutive rows that share a group fold into one collapsible entry. */
function groupAreas(areas: AreaRow[]): GridEntry[] {
  const entries: GridEntry[] = [];
  for (const area of areas) {
    const last = entries[entries.length - 1];
    if (area.group && last?.kind === "group" && last.label === area.group) {
      last.areas.push(area);
    } else if (area.group) {
      entries.push({ kind: "group", label: area.group, areas: [area] });
    } else {
      entries.push({ kind: "single", area });
    }
  }
  return entries;
}

/**
 * One role's View/Edit per area. Standalone pages are a row each; a sidebar
 * group is one summary row, closed by default, that opens into a row per page.
 *
 * The group row's switches act on every page inside it — the quick way to give
 * a role a whole section — and read as "on" only when every page is on. A
 * partial group says so under the switch rather than pretending either way.
 */
function RoleGrid({
  role,
  areas,
}: {
  role: MatrixRole;
  areas: AccessMatrix["areas"];
}) {
  const {
    mutate: setCell,
    mutateAsync: setCellAsync,
    isPending,
  } = useSetAccessCell();
  const entries = useMemo(() => groupAreas(areas), [areas]);
  // Groups start closed: the sub-rows are the detail, the group row the summary.
  // Which ones are open lives in the URL (?open=users,forms) beside ?role=, so a
  // reload, the back button and a shared link all show the same rows — the same
  // reason the role picker keeps its choice there.
  const [searchParams, setSearchParams] = useSearchParams();
  const openGroups = openGroupsFrom(searchParams);
  const toggleGroup = (group: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const open = openGroupsFrom(next);
        const key = groupKey(group);
        if (open.has(key)) open.delete(key);
        else open.add(key);
        if (open.size) next.set("open", [...open].join(","));
        else next.delete("open");
        return next;
      },
      { replace: true },
    );

  const set = (
    area: AreaRow,
    capability: Capability,
    value: boolean,
    groupLabel?: string,
  ) =>
    setCell({
      area: area.key,
      role: role.name,
      capability,
      value,
      // Pins the write to the cell as last read, so two people fighting over
      // the SAME box conflict while different boxes never do.
      expected_version: role.cells[area.key]?.version,
      // Present only for a group switch: silences the per-page toast so the
      // group can be announced once, under the name that was actually clicked.
      groupLabel,
    });

  const cellSwitch = (area: AreaRow, capability: Capability) => {
    const cell = role.cells[area.key];
    return (
      <Switch
        aria-label={`${label(role.name)} ${capability} ${area.label}`}
        checked={cell?.[capability] ?? false}
        disabled={!cell || isPending}
        onCheckedChange={(value) => set(area, capability, value)}
      />
    );
  };

  /**
   * A group switch writes one row per page underneath it, then says so once.
   *
   * The sentence describes the GROUP because that is what was clicked. What the
   * pages ended up with is the server's "Edit implies View" rule applied to
   * every one of them, so the pair named is derived from the click rather than
   * read back per page: granting Edit grants View with it, revoking View takes
   * Edit with it. A page that was already in the target state is not written at
   * all, and if every page was, nothing is claimed.
   */
  const applyToGroup = async (
    entry: Extract<GridEntry, { kind: "group" }>,
    capability: Capability,
    value: boolean,
  ) => {
    const changing = entry.areas.filter(
      (a) => (role.cells[a.key]?.[capability] ?? false) !== value,
    );
    if (!changing.length) return;

    const results = await Promise.allSettled(
      changing.map((area) => setCellAsync({
        area: area.key,
        role: role.name,
        capability,
        value,
        expected_version: role.cells[area.key]?.version,
        groupLabel: entry.label,
      })),
    );

    // Every page refused? Each refusal already raised its own error toast.
    const saved = results.some(
      (r) => r.status === "fulfilled" && !r.value?.unchanged,
    );
    if (!saved) return;

    // The four cases, spelled out rather than computed: this is the server's
    // Edit-implies-View rule, and a clever expression for it would be one
    // negation away from telling the operator the wrong thing.
    const on: AreaAccess = { view: true, edit: true };
    const off: AreaAccess = { view: false, edit: false };
    const { before, after } =
      capability === "view"
        ? value
          ? { before: off, after: { view: true, edit: false } } // View alone
          : { before: on, after: off } // View goes, Edit with it
        : value
        ? { before: off, after: on } // Edit grants View too
        : { before: on, after: { view: true, edit: false } }; // Edit alone

    toast({
      ...describeAccessChange({
        roleLabel: label(role.name),
        areaLabel: entry.label,
        scope: "group",
        before,
        after,
        granted: value,
      }),
      className: "border-orange-500",
    });
  };

  const groupSwitch = (entry: Extract<GridEntry, { kind: "group" }>, capability: Capability) => {
    const cells = entry.areas.map((a) => role.cells[a.key]);
    const on = cells.filter((c) => c?.[capability]).length;
    const all = on === entry.areas.length;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <Switch
          aria-label={`${label(role.name)} ${capability} all of ${entry.label}`}
          checked={all}
          disabled={isPending || cells.some((c) => !c)}
          // Only the pages that differ are written, one request each — then ONE
          // toast, naming the group. Announced after the writes rather than on
          // the click, so a refusal (the last-admin guard, a stale version) is
          // reported by its own error toast instead of a false success.
          onCheckedChange={(value) => void applyToGroup(entry, capability, value)}
        />
        {!all && on > 0 && (
          <span className="text-[10px] leading-none text-muted-foreground">
            {on} of {entry.areas.length}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Area</TableHead>
              <TableHead className="w-28 text-center">View</TableHead>
              <TableHead className="w-28 text-center">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => {
              if (entry.kind === "single") {
                return (
                  <TableRow key={entry.area.key}>
                    <TableCell className="font-medium">
                      {entry.area.label}
                    </TableCell>
                    {CAPABILITIES.map((capability) => (
                      <TableCell key={capability} className="text-center">
                        {cellSwitch(entry.area, capability)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              }

              const isOpen = openGroups.has(groupKey(entry.label));
              return (
                <Fragment key={entry.label}>
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.label)}
                        aria-expanded={isOpen}
                        className="flex items-center gap-2 text-left"
                      >
                        <ChevronRight
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                            isOpen ? "rotate-90" : ""
                          }`}
                        />
                        <span>{entry.label}</span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {entry.areas.length} pages
                        </span>
                      </button>
                    </TableCell>
                    {CAPABILITIES.map((capability) => (
                      <TableCell key={capability} className="text-center">
                        {groupSwitch(entry, capability)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isOpen &&
                    entry.areas.map((area) => (
                      <TableRow key={area.key}>
                        <TableCell className="pl-10">{area.label}</TableCell>
                        {CAPABILITIES.map((capability) => (
                          <TableCell key={capability} className="text-center">
                            {cellSwitch(area, capability)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        Access applies to all regions. Changes save as soon as you click and
        take effect immediately — there is no save button. Granting Edit also
        grants View, and removing View also removes Edit. Removing Edit means
        the server refuses those changes, not just that buttons disappear. A
        group&apos;s switch sets every page inside it; open the group to set
        pages one by one.
      </p>
    </div>
  );
}
